import { useCallback, useEffect, useRef, useState, RefObject } from "react";
// scrollActivity is still plain JS — its exports come in as `any`, which is fine.
import { subscribeScrollActivity, isScrolling } from "../utils/scrollActivity";

// ── Animation tuning ────────────────────────────────────────────────────────
const VELOCITY_PX_PER_S = 40; // scroll speed
const PAUSE_MS = 5000; // pause before scrolling again
export const GAP_PX = 48; // distance between the end of the text and its wrapped copy

// Wait before the FIRST scroll so the list has finished mounting/painting and
// the cards' layers are promoted (willChange) before any motion starts. A
// random jitter staggers many cards so they don't all kick off on one frame.
const ANIM_START_DELAY_MS = 900;
const ANIM_START_JITTER_MS = 700;

// The overflow MEASURE is also deferred (one frame after paint, plus a small
// per-card jitter). Without this, the first time the task list paints, every
// belt title reads layout (offsetWidth) and promotes a compositor layer on the
// same frame — that synchronous burst is the start-up freeze the user sees.
// Deferring it lets the cards paint instantly as plain static titles; the belt
// sets itself up a beat later.
const MEASURE_JITTER_MS = 250;

// Edge-fade gradient, applied imperatively to the container while scrolling so
// the moving (compositor) layer is never interrupted by a React re-render.
const BELT_MASK =
  "linear-gradient(to right, transparent 0, #000 12px, #000 calc(100% - 12px), transparent 100%)";

export interface BeltMarquee {
  containerRef: RefObject<HTMLDivElement>;
  trackRef: RefObject<HTMLDivElement>;
  copyRef: RefObject<HTMLSpanElement>;
  /** True when the title overflows and therefore scrolls (2nd copy rendered). */
  overflowing: boolean;
}

/**
 * Behaviour for the warping marquee ("belt") title: measures overflow, runs the
 * WAAPI scroll/pause loop, drives the edge fade imperatively, and pauses while
 * the list scrolls vertically. Pure logic — the component just wires the
 * returned refs + `overflowing` flag into the DOM.
 */
export function useBeltMarquee(text: string): BeltMarquee {
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLSpanElement>(null);
  const animRef = useRef<Animation | null>(null);
  const timingRef = useRef<{ scrollMs: number; total: number }>({ scrollMs: 0, total: 0 });
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The ONLY React state here — flips once after the deferred measure. Scrolling
  // and the fade are driven imperatively below, so they never re-render.
  const [overflowing, setOverflowing] = useState(false);

  const applyFade = useCallback((on: boolean) => {
    const el = containerRef.current;
    if (!el) return;
    // setProperty avoids depending on vendor-prefixed typings.
    el.style.setProperty("-webkit-mask-image", on ? BELT_MASK : "none");
    el.style.setProperty("mask-image", on ? BELT_MASK : "none");
  }, []);

  const clearFadeSchedule = useCallback(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  // Fade ON while scrolling, OFF during the pause. Reads the animation's
  // currentTime once and schedules a single timeout for the next phase
  // boundary, which re-arms itself — ~2 toggles/cycle, not a polling loop.
  const syncFade = useCallback(() => {
    clearFadeSchedule();
    const a = animRef.current;
    const { scrollMs, total } = timingRef.current;
    if (!a || a.playState !== "running" || !total) {
      applyFade(false);
      return;
    }
    const t = (Number(a.currentTime) || 0) % total;
    const inScroll = t < scrollMs;
    applyFade(inScroll);
    const msToBoundary = (inScroll ? scrollMs - t : total - t) + 16;
    fadeTimerRef.current = setTimeout(syncFade, msToBoundary);
  }, [applyFade, clearFadeSchedule]);

  // Phase 1 — measure (deferred): does the title overflow? Reads layout one
  // frame AFTER the list paints (plus a per-card jitter), so the first paint
  // isn't a burst of synchronous offsetWidth reads + layer promotions across
  // every card. We do NOT start the animation here — that's phase 2.
  useEffect(() => {
    if (animRef.current) {
      animRef.current.cancel();
      animRef.current = null;
    }
    clearFadeSchedule();
    applyFade(false);
    setOverflowing(false);

    let raf = 0;
    const measure = () => {
      const c = containerRef.current;
      const copy = copyRef.current;
      if (!c || !copy) return;
      setOverflowing(copy.offsetWidth > c.clientWidth + 1);
    };
    const timer = setTimeout(() => {
      raf = requestAnimationFrame(measure);
    }, Math.random() * MEASURE_JITTER_MS);

    return () => {
      clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [text, applyFade, clearFadeSchedule]);

  // Phase 2 — animate: runs only when overflowing, i.e. AFTER the 2nd copy is in
  // the DOM, so the animated layer never relayouts once moving. Delayed by a
  // timer so the list settles and the layer is promoted before motion starts.
  useEffect(() => {
    if (!overflowing) return;
    const track = trackRef.current;
    const copy = copyRef.current;
    if (!track || !copy || typeof track.animate !== "function") return;

    // Translate by one copy + the gap; at that point the 2nd copy sits exactly
    // where the 1st started, so restarting at 0 is invisible → seamless wrap.
    const distance = copy.offsetWidth + GAP_PX;
    const scrollMs = (distance / VELOCITY_PX_PER_S) * 1000;
    const total = scrollMs + PAUSE_MS;
    timingRef.current = { scrollMs, total };
    const end = `translateX(-${distance}px)`;
    const keyframes = [
      { transform: "translateX(0)", offset: 0 },
      { transform: end, offset: scrollMs / total }, // wrapped one full copy
      { transform: end, offset: 1 }, // pause, then loop (jump back is invisible)
    ];

    let cancelled = false;
    const delay = ANIM_START_DELAY_MS + Math.random() * ANIM_START_JITTER_MS;
    const timer = setTimeout(() => {
      if (cancelled || !trackRef.current) return;
      const anim = track.animate(keyframes, {
        duration: total,
        iterations: Infinity,
        easing: "linear",
      });
      animRef.current = anim;
      if (isScrolling()) {
        anim.pause(); // don't start mid vertical-scroll
      } else {
        syncFade(); // fade correct from frame 0 — no late "pop"
      }
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearFadeSchedule();
      if (animRef.current) {
        animRef.current.cancel();
        animRef.current = null;
      }
      applyFade(false);
    };
  }, [overflowing, text, applyFade, clearFadeSchedule, syncFade]);

  // Pause the marquee (and drop the fade) while the list scrolls vertically.
  useEffect(() => {
    const unsubscribe = subscribeScrollActivity((active: boolean) => {
      const a = animRef.current;
      if (!a) return;
      if (active) {
        a.pause();
        clearFadeSchedule();
        applyFade(false);
      } else {
        a.play();
        syncFade();
      }
    });
    // Wrap so the effect's destructor returns void (subscribe returns the
    // Set.delete boolean, which React's EffectCallback type rejects).
    return () => {
      unsubscribe();
    };
  }, [applyFade, clearFadeSchedule, syncFade]);

  return { containerRef, trackRef, copyRef, overflowing };
}
